import { isObject, isArray } from '../../utils/typeUtils';
import { conf } from '../configService';
import { queryService } from '../queryService';
import { theme } from '../../theme';
import { adSizes } from './config/adSizes';
import { adIds } from './config/adIds';
import { gptService } from '../gptService';

const cache = {
  adFormatsByBreakpoint: null,
  adSizesByBreakpoint: null,
  sharedSizes: {},
  styles: {},
  availableSizes: {}
};

const validPTTs = [
  'animals',
  'space',
  'photography',
  'explorers',

  'video',
  'television-and-video',

  'science',
  'science-and-technology',

  'travel',
  'travel-and-adventure',

  'environment',
  'environment-and-conservation',

  'history',
  'history-and-civilization',
  'history-and-culture'
];

const pttRenames = {
  'television-and-video': 'video',
  'science-and-technology': 'science',
  'environment-and-conservation': 'environment',
  'history-and-civilization': 'history',
  'history-and-culture': 'history',
  'travel-and-adventure': 'travel'
};

/**
 * Get ad unit code
 *
 * @param {object} content - Content data.
 * @returns {string} - Ad unit code
 */
const getAdUnitCode = content => {
  if (!content) {
    return '';
  }

  // Homepage has a fixed value
  if (content.bundle === 'homepage') {
    return '/homepage';
  }

  // Use ancestorsSlug or englishUrl otherwise
  const taxonomiesSource =
    content.primaryTaxonomy?.entity?.ancestorsSlug ||
    content.primaryTaxonomy?.entity?.englishUrl;

  if (!taxonomiesSource) {
    return '';
  }

  // travel-and-adventure/adventure has a fixed value
  if (taxonomiesSource === 'travel-and-adventure/adventure') {
    return '/adventure';
  }

  // Remove leading slash
  const taxonomies = taxonomiesSource.replace(/^\//, '');

  const foundTaxonomy = taxonomies
    .split('/')
    .find(item => validPTTs.includes(item));

  if (foundTaxonomy) {
    return `/${pttRenames[foundTaxonomy] || foundTaxonomy}`;
  }

  return '';
};

/**
 * Flattens a multidimensional array keeping inner arrays.
 *
 * Transforms arrays like this [[[1, 2], [3, 4]], [5, 6]] into
 * this [[1, 2], [3, 4], [5, 6]]
 *
 * This is a specific method for this service, do not move it to arrayUtils
 *
 * @param {array} array - Multidimensional array
 * @param {array} stack - Array to hold results
 * @returns {array} - Flatten array
 */
const flattenArray = (array, stack = []) => {
  array.forEach(item =>
    isArray(item) ? flattenArray(item, stack) : stack.push(array)
  );
  return stack;
};

/**
 * Get all widths from an array of sizes
 *
 * Transforms [[1, 2], [3, 4], [5, 6], [7, 8]] into [2, 4 , 6, 8]
 *
 * @param {array} sizes - Array of sizes
 * @returns {array} - Array of widths
 */
/* const getAllWidths = sizes =>
  isArray(sizes[0]) ? sizes.map(s => s[0]) : [sizes[0]];
 */

/**
 * Get all heights from an array of sizes
 *
 * Transforms [[1, 2], [3, 4], [5, 6], [7, 8]] into [1, 3, 5, 7]
 *
 * @param {array} sizes - Array of sizes
 * @returns {array} - Array of heights
 */
const getAllHeights = sizes =>
  isArray(sizes[0]) ? sizes.map(s => s[1]) : [sizes[1]];

export const adService = {
  /**
   * Get object with sizes for each breakpoint
   *
   * @param {string} id - Ad id.
   * @returns {object} - Object with sizes for each breakpoint
   */
  getBreakpointSizes: id => {
    // Normalize adIds the first time this function is called
    if (!cache.adFormatsByBreakpoint) {
      cache.adFormatsByBreakpoint = adService.getAdFormatsByBreakpoint();
    }
    if (!cache.adSizesByBreakpoint) {
      cache.adSizesByBreakpoint = adService.getAdSizesByBreakpoint(
        cache.adFormatsByBreakpoint
      );
    }

    const sizes = cache.adSizesByBreakpoint[id];
    if (!sizes) {
      throw Error(`Unknown ad id "${id}"`);
    }

    return sizes;
  },

  /**
   * Normalizes adIds structure into an object of breakpoint objects
   *
   * Transforms strings, arrays of strings and incomplete breakpoint objects
   * into an object of full breakpoint objects
   *
   * @returns {object} - Object of full breakpoint objects
   */
  getAdFormatsByBreakpoint: () => {
    const adFormatsByBreakpoint = {};
    Object.keys(adIds).forEach(id => {
      if (isObject(adIds[id])) {
        theme.breakpoints.sorted.forEach(bp => {
          if (!adFormatsByBreakpoint[id]) {
            adFormatsByBreakpoint[id] = {};
          }
          adFormatsByBreakpoint[id][bp.name] = adIds[id][bp.name] || null;
        });
      } else {
        theme.breakpoints.sorted.forEach(bp => {
          if (!adFormatsByBreakpoint[id]) {
            adFormatsByBreakpoint[id] = {};
          }
          adFormatsByBreakpoint[id][bp.name] = adIds[id];
        });
      }
    });
    return adFormatsByBreakpoint;
  },

  /**
   * Add ad sizes to the structure of breakpoint objects
   *
   * @returns {object} - Object of full breakpoint objects
   */
  getAdSizesByBreakpoint: () => {
    // cache.adFormatsByBreakpoint
    const adSizesByBreakpoint = {};
    Object.keys(cache.adFormatsByBreakpoint).forEach(adId => {
      adSizesByBreakpoint[adId] = {};
      Object.keys(cache.adFormatsByBreakpoint[adId]).forEach(bp => {
        adSizesByBreakpoint[adId][bp] = {};
        const adFormats = cache.adFormatsByBreakpoint[adId][bp];
        if (Array.isArray(adFormats)) {
          // This is used to able to reference the same array so
          // equality comparisons between breakpoints in the same
          // element return true.
          const adFormatsCacheKey = adFormats.join('__');
          if (!cache.sharedSizes[adFormatsCacheKey]) {
            const sizes = flattenArray(
              adFormats.map(adFormat => adSizes[adFormat])
            );
            // Some values can be repeated, use Set to avoid them
            cache.sharedSizes[adFormatsCacheKey] = [...new Set(sizes)];
          }
          adSizesByBreakpoint[adId][bp] = cache.sharedSizes[adFormatsCacheKey];
        } else {
          adSizesByBreakpoint[adId][bp] = adSizes[adFormats];
        }
      });
    });
    return adSizesByBreakpoint;
  },

  /**
   * Get all possible Ad sizes.
   *
   * @param {string} id - Ad id.
   * @returns {array} - An array of sizes ([[1, 2], [3, 4], [5, 6], [7, 8]])
   */
  getAllAvailableSizes: id => {
    if (cache.availableSizes[id]) {
      return cache.availableSizes[id];
    }

    const sizes = adService.getBreakpointSizes(id);
    const stack = [];
    Object.keys(sizes).forEach(
      bp => sizes[bp] && flattenArray(sizes[bp]).forEach(x => stack.push(x))
    );

    // Some values can be repeated, use Set to avoid them
    const availableSizes = [...new Set(stack)];

    cache.availableSizes[id] = availableSizes;
    return availableSizes;
  },

  /**
   * Get first size of all possible Ad sizes.
   *
   * @param {string} id - Ad id.
   * @returns {array} - An array of sizes ([1, 2])
   */
  getFirstSize: id => {
    const allAvailableSizes = adService.getAllAvailableSizes(id);
    return isArray(allAvailableSizes[0])
      ? allAvailableSizes[0]
      : allAvailableSizes;
  },

  /**
   * Get responsive styles for an ad
   *
   * @param {string} id - Ad id.
   * @param {object} options - Object of options.
   * @returns {object} - Object with styles for each breakpoint
   */
  getStyles: (id, options = { isCollapsable: false }) => {
    const cacheId = `${id}--${JSON.stringify(options)}`;
    if (cache.styles[cacheId]) {
      return cache.styles[cacheId];
    }

    const displayModifierRule = options.isCollapsable ? '' : ' !important';
    const sizes = adService.getBreakpointSizes(id);
    const bpStyles = {};
    let previousBpStyle;
    theme.breakpoints.sorted.forEach(bp => {
      let style;
      if (sizes[bp.name]) {
        const allHeights = getAllHeights(sizes[bp.name]);
        const maxHeight = Math.max(...allHeights);
        const minHeight = Math.min(...allHeights);
        // If max and min height are the same, or if they are quite similar,
        // use the larger one to avoid reflows.
        // If id is "header", use a fixed height to avoid reflows.
        if (
          id === 'header' ||
          maxHeight === minHeight ||
          (minHeight / maxHeight) * 100 < 1.5
        ) {
          style = `display:grid${displayModifierRule}; place-items: center; height: ${maxHeight}px;`;
        } else {
          style = `display:grid${displayModifierRule}; place-items: center; height:auto; min-height: ${minHeight}px; max-height: ${maxHeight}px;`;
        }
      } else {
        style = `display: none${displayModifierRule};`;
      }
      if (previousBpStyle !== style) {
        bpStyles[bp.name] = style;
      }
      previousBpStyle = style;
    });

    cache.styles[id] = bpStyles;
    return bpStyles;
  },

  /**
   * Tells whether an ad is showable on a breakpoint
   *
   * @param {string} id - Ad id.
   * @param {string} breakpointName - Breakpoint name.
   * @returns {object} - Object with styles for each breakpoint
   */
  isShowableOn: (id, breakpointName) =>
    !!adService.getBreakpointSizes(id)[breakpointName],

  /**
   * Get ad unit path
   *
   * @param {object} content - Content data.
   * @returns {string} - Ad unit path
   */
  getAdUnitPath: content => {
    const domain = conf.canonicalDomain.replace('www.', '');
    return `/${conf.gamAccountId}/${domain}/web${getAdUnitCode(content)}`;
  },

  /**
   * Get ad targeting
   *
   * "adSlotId" and "adId" are mutually exclusive params, but can be both of them null
   * if no "pos" KVP is required for targeting (e.g.- videos and video player).
   *
   * @param {object} content - Content data.
   * @param {string} adSlotId - optional if adId is present (i.e.- gpt--header__variant--12345, etc).
   *   Use this param when the ad has been loaded and you need to show its CURRENT position
   *   for targeting (i.e.- in AdInfo.jsx).
   * @param {string} adId - optional if adSlotId is present (i.e.- header, etc). Based on adIds.js file.
   *   Use this param when the ad has NOT been loaded and you need to get a NEW position
   *   for targeting (i.e.- in Ad.jsx).
   * @returns {object} - Ad targeting data
   */
  getTargeting: (content, adSlotId, adId) => {
    const targeting = {
      test: 'refresh',
      Ed: conf.siteCountryCode,
      lang: conf.langcodeIso639_1
      // isDnt: 0 // Zero until we get more info about it.
    };
    if (adSlotId || adId) {
      targeting.pos = adSlotId
        ? gptService.getAdSlotPosition(adSlotId)
        : gptService.getAdSlotsByAdId(adId).length + 1;
    }

    if (content) {
      targeting.objid = `NG_INT_${conf.siteCountryCode.toUpperCase()}_${
        content.id ||
        (content.url?.path ? content.url?.path.replace('/', '') : '')
      }`;
    }

    const tstAdTar = queryService.getParameter('tstAdTar');
    if (tstAdTar) {
      targeting.tstAdTar = tstAdTar;
    }

    if (content && content.sponsoredAd) {
      targeting.tag = content.sponsoredAd;
    }

    return targeting;
  },

  getAdSizes: () => adSizes
};
